import { ConfigStore } from '../config/store';
import {
  SCHEMA,
  isAttributeVisible,
  type Attribute,
  type AttrOption,
} from '../config/schema';
import { resolveConfig } from '../config/resolve';
import { computeQuote, formatEUR } from '../config/quote';
import { configSummaryRows } from '../config/summary';
import pricingData from '../config/pricing.json';

/** Acciones que el wizard delega al exterior (p. ej. generar la ficha-resumen). */
export interface WizardActions {
  onGenerateSummary?: (format: 'png' | 'pdf') => void | Promise<void>;
  onSaveQuote?: () => Promise<{ ok: boolean; via: 'server' | 'local'; message: string }>;
}

const PREMIUM_SURCHARGE: number = (pricingData as { recargos: { color_tier: { premium: number } } })
  .recargos.color_tier.premium;

/**
 * Wizard de configuración por pasos. Lee el esquema (attributes.json) y el store,
 * renderiza los controles con resaltado de selección y navega entre pasos.
 * Re-renderiza al cambiar de paso o de modo de dimensiones; el resto de cambios
 * actualizan el resaltado in situ (sin perder el foco de los inputs).
 */
export class Wizard {
  private priceValueEl: HTMLElement | null = null;
  private saveStatusEl: HTMLElement | null = null;

  constructor(
    private readonly root: HTMLElement,
    private readonly store: ConfigStore,
    private readonly actions: WizardActions = {},
  ) {
    this.store.subscribe((c) => {
      // Re-render si cambia el paso o un atributo del que depende la visibilidad de otros.
      if (c.key === 'step' || c.key === 'dimensiones-modo' || c.key === 'led') this.render();
      else this.updatePrice();
    });
    this.render();
  }

  private render(): void {
    const steps = SCHEMA.steps;
    const idx = this.store.step;
    const step = steps[idx];

    this.root.innerHTML = '';
    const panel = el('div', 'wizard');

    // --- Cabecera: título del paso + n/total + flechas ---
    const header = el('div', 'wz-header');
    const arrows = el('div', 'wz-arrows');
    const prev = button('‹', 'wz-arrow', () => this.go(idx - 1));
    prev.disabled = idx === 0;
    const next = button('›', 'wz-arrow', () => this.go(idx + 1));
    next.disabled = idx === steps.length - 1;
    arrows.append(prev, next);
    const titles = el('div', 'wz-titles');
    titles.append(
      textEl('span', 'wz-step-label', step.label),
      textEl('span', 'wz-step-count', `${idx + 1}/${steps.length}`),
    );
    header.append(titles, arrows);
    panel.append(header);

    // --- Cuerpo del paso ---
    const body = el('div', 'wz-body');
    if (step.id === 'resumen') {
      body.append(this.renderSummary());
    } else {
      for (const attrId of step.attributes) {
        if (!isAttributeVisible(attrId, this.store.getState())) continue;
        body.append(this.renderAttribute(attrId, SCHEMA.attributes[attrId]));
      }
    }
    panel.append(body);

    // --- Barra de precio en vivo (siempre visible) ---
    panel.append(this.buildPriceBar());

    // --- Pie: navegación ---
    const footer = el('div', 'wz-footer');
    if (idx > 0) footer.append(button('Anterior', 'wz-btn wz-btn-ghost', () => this.go(idx - 1)));
    if (idx < steps.length - 1) {
      footer.append(button('Siguiente', 'wz-btn wz-btn-primary', () => this.go(idx + 1)));
    }
    panel.append(footer);

    this.root.append(panel);
  }

  private go(index: number): void {
    const max = SCHEMA.steps.length - 1;
    this.store.setStep(Math.max(0, Math.min(max, index)));
  }

  // ---------- Render de un atributo ----------
  private renderAttribute(id: string, attr: Attribute): HTMLElement {
    const group = el('div', 'wz-attr');
    group.append(textEl('div', 'wz-attr-label', attr.label));

    if (attr.type === 'decimal') {
      group.append(this.renderDecimal(id, attr));
    } else if (attr.ui === 'icon-cards') {
      group.append(this.renderIconCards(id, attr));
    } else if (attr.ui === 'color-swatches') {
      group.append(this.renderSwatches(id, attr));
    } else if (attr.type === 'multi-select') {
      group.append(this.renderMultiSelect(id, attr));
    } else {
      group.append(this.renderSingleSelect(id, attr));
    }
    return group;
  }

  /** Tarjetas con icono (tipo, rotación de lamas, LED). */
  private renderIconCards(id: string, attr: Attribute): HTMLElement {
    const wrap = el('div', 'wz-cards');
    const current = this.store.get<string>(id);
    for (const opt of attr.options ?? []) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'wz-card';
      const ico = el('span', 'wz-card-ico');
      ico.innerHTML = ICONS[opt.icon ?? ''] ?? '';
      card.append(ico, textEl('span', 'wz-card-label', opt.label));
      if (opt.value === current) card.classList.add('selected');
      card.addEventListener('click', () => {
        this.store.set(id, opt.value);
        wrap.querySelectorAll('.wz-card').forEach((n) => n.classList.remove('selected'));
        card.classList.add('selected');
      });
      wrap.append(card);
    }
    return wrap;
  }

  private renderSingleSelect(id: string, attr: Attribute): HTMLElement {
    const wrap = el('div', 'wz-options');
    const current = this.store.get<string>(id);
    for (const opt of attr.options ?? []) {
      const b = button(opt.label, 'wz-option', () => {
        this.store.set(id, opt.value);
        wrap.querySelectorAll('.wz-option').forEach((n) => n.classList.remove('selected'));
        b.classList.add('selected');
      });
      if (opt.value === current) b.classList.add('selected');
      wrap.append(b);
    }
    return wrap;
  }

  private renderMultiSelect(id: string, attr: Attribute): HTMLElement {
    const wrap = el('div', 'wz-options');
    const selected = new Set(this.store.get<string[]>(id) ?? []);
    for (const opt of attr.options ?? []) {
      const b = button(opt.label, 'wz-option', () => {
        if (selected.has(opt.value)) selected.delete(opt.value);
        else selected.add(opt.value);
        b.classList.toggle('selected');
        this.store.set(id, [...selected]);
      });
      if (selected.has(opt.value)) b.classList.add('selected');
      wrap.append(b);
    }
    return wrap;
  }

  private renderDecimal(id: string, attr: Attribute): HTMLElement {
    const wrap = el('div', 'wz-decimal');
    const value = Number(this.store.get(id));
    const min = attr.min ?? 0;
    const max = attr.max ?? 10000;
    const stepv = attr.step ?? 1;

    const range = document.createElement('input');
    range.type = 'range';
    range.className = 'wz-range';
    range.min = String(min);
    range.max = String(max);
    range.step = String(stepv);
    range.value = String(value);

    const numberRow = el('div', 'wz-number-row');
    const num = document.createElement('input');
    num.type = 'number';
    num.className = 'wz-number';
    num.min = String(min);
    num.max = String(max);
    num.step = String(stepv);
    num.value = String(value);
    numberRow.append(num, textEl('span', 'wz-unit', attr.unit ?? ''));

    const hint = textEl('div', 'wz-hint', `min ${min} · max ${max} ${attr.unit ?? ''}`);

    const commit = (raw: number): void => {
      const clamped = Math.min(max, Math.max(min, raw || min));
      range.value = String(clamped);
      num.value = String(clamped);
      this.store.set(id, clamped);
    };
    range.addEventListener('input', () => commit(Number(range.value)));
    num.addEventListener('change', () => commit(Number(num.value)));

    wrap.append(range, numberRow, hint);
    return wrap;
  }

  private renderSwatches(id: string, attr: Attribute): HTMLElement {
    const wrap = el('div', 'wz-swatches-wrap');
    const current = this.store.get<string>(id);

    const hasTiers = (attr.options ?? []).some((o) => o.tier);
    const groups: Array<{ tier?: 'estandar' | 'premium'; title?: string }> = hasTiers
      ? [
          { tier: 'estandar', title: 'Estándar' },
          { tier: 'premium', title: `Premium (+${PREMIUM_SURCHARGE} €)` },
        ]
      : [{}];

    const select = (opt: AttrOption, node: HTMLElement): void => {
      this.store.set(id, opt.value);
      wrap.querySelectorAll('.wz-swatch').forEach((n) => n.classList.remove('selected'));
      node.classList.add('selected');
    };

    for (const g of groups) {
      const opts = g.tier ? (attr.options ?? []).filter((o) => o.tier === g.tier) : attr.options ?? [];
      if (opts.length === 0) continue;
      if (g.title) wrap.append(textEl('div', 'wz-swatch-group', g.title));
      const grid = el('div', 'wz-swatches');
      for (const opt of opts) {
        const sw = document.createElement('button');
        sw.type = 'button';
        sw.className = 'wz-swatch';
        sw.title = `${opt.ral} · ${opt.label}`;
        const chip = el('span', 'wz-chip');
        chip.style.background = opt.hex ?? '#888';
        const meta = el('span', 'wz-swatch-meta');
        meta.append(textEl('span', 'wz-swatch-ral', opt.ral ?? opt.label));
        if (opt.ral) meta.append(textEl('span', 'wz-swatch-name', opt.label));
        sw.append(chip, meta);
        if (opt.value === current) sw.classList.add('selected');
        sw.addEventListener('click', () => select(opt, sw));
        grid.append(sw);
      }
      wrap.append(grid);
    }
    return wrap;
  }

  // ---------- Precio en vivo ----------
  private buildPriceBar(): HTMLElement {
    const quote = computeQuote(resolveConfig(this.store.getState()));
    const bar = el('div', 'wz-pricebar');
    const left = el('div', 'wz-price-left');
    left.append(
      textEl('span', 'wz-price-cap', 'Precio estimado'),
      textEl('span', 'wz-price-sub', 'IVA (21%) incluido'),
    );
    this.priceValueEl = textEl('span', 'wz-price-value', formatEUR(quote.total));
    bar.append(left, this.priceValueEl);
    return bar;
  }

  private updatePrice(): void {
    if (!this.priceValueEl) return;
    const quote = computeQuote(resolveConfig(this.store.getState()));
    this.priceValueEl.textContent = formatEUR(quote.total);
    // Si estamos en el paso Resumen, refrescar el desglose.
    if (SCHEMA.steps[this.store.step]?.id === 'resumen') this.render();
  }

  // ---------- Resumen ----------
  private renderSummary(): HTMLElement {
    const state = this.store.getState();
    const cfg = resolveConfig(state);

    const wrap = el('div', 'wz-summary');
    const rows = configSummaryRows(state);
    for (const [k, v] of rows) {
      const row = el('div', 'wz-sum-row');
      row.append(textEl('span', 'wz-sum-k', k), textEl('span', 'wz-sum-v', v));
      wrap.append(row);
    }

    // Desglose de presupuesto por partidas.
    const quote = computeQuote(cfg);
    wrap.append(textEl('div', 'wz-breakdown-title', 'Desglose del presupuesto'));
    const bd = el('div', 'wz-breakdown');
    for (const line of quote.lines) {
      bd.append(this.quoteRow(line.label, formatEUR(line.amount)));
    }
    bd.append(this.quoteRow('Subtotal', formatEUR(quote.subtotal), 'wz-bd-subtotal'));
    if (quote.minApplied) {
      bd.append(this.quoteRow('Precio mínimo aplicado', formatEUR(quote.base), 'wz-bd-min'));
    }
    bd.append(this.quoteRow('IVA (21%)', formatEUR(quote.iva)));
    bd.append(this.quoteRow('Total (PVP)', formatEUR(quote.total), 'wz-bd-total'));
    wrap.append(bd);

    // Acciones: generar la ficha-resumen.
    if (this.actions.onGenerateSummary) {
      const actions = el('div', 'wz-sum-actions');
      const png = button('Generar resumen (PNG)', 'wz-btn wz-btn-primary', () =>
        this.runGenerate('png', png),
      );
      const pdf = button('Descargar PDF', 'wz-btn wz-btn-ghost', () => this.runGenerate('pdf', pdf));
      actions.append(png, pdf);
      wrap.append(actions);
    }

    // Acción: guardar / solicitar presupuesto.
    if (this.actions.onSaveQuote) {
      const save = button(
        'Guardar configuración / Solicitar presupuesto',
        'wz-btn wz-btn-save',
        () => this.runSave(save),
      );
      wrap.append(save);
      const status = el('div', 'wz-save-status');
      this.saveStatusEl = status;
      wrap.append(status);
    }

    wrap.append(
      textEl('div', 'wz-sum-note', 'La ficha incluye las 4 vistas acotadas, la configuración, el precio y los datos de Teyco.'),
    );
    return wrap;
  }

  private async runSave(btn: HTMLButtonElement): Promise<void> {
    if (!this.actions.onSaveQuote) return;
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Guardando…';
    if (this.saveStatusEl) this.saveStatusEl.textContent = '';
    try {
      const res = await this.actions.onSaveQuote();
      if (this.saveStatusEl) {
        this.saveStatusEl.textContent = res.message;
        this.saveStatusEl.className = `wz-save-status ${res.via === 'server' ? 'ok' : 'warn'}`;
      }
    } catch (err) {
      console.error('[Teyco] Error guardando el presupuesto:', err);
      if (this.saveStatusEl) {
        this.saveStatusEl.textContent = 'Error al guardar el presupuesto.';
        this.saveStatusEl.className = 'wz-save-status err';
      }
    } finally {
      btn.disabled = false;
      btn.textContent = prev;
    }
  }

  private async runGenerate(format: 'png' | 'pdf', btn: HTMLButtonElement): Promise<void> {
    if (!this.actions.onGenerateSummary) return;
    const prev = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Generando…';
    try {
      await this.actions.onGenerateSummary(format);
    } catch (err) {
      console.error('[Teyco] Error generando la ficha:', err);
    } finally {
      btn.disabled = false;
      btn.textContent = prev;
    }
  }

  private quoteRow(label: string, amount: string, extraClass = ''): HTMLElement {
    const row = el('div', `wz-bd-row ${extraClass}`.trim());
    row.append(textEl('span', 'wz-bd-k', label), textEl('span', 'wz-bd-v', amount));
    return row;
  }
}

// ---------- iconos (line-art, heredan color con currentColor) ----------
const ICONS: Record<string, string> = {
  'tipo-libre':
    '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M7 11h34M10 11v17M38 11v17"/><path d="M12 8h24" stroke-opacity="0.45"/></svg>',
  'tipo-muro':
    '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 11h30M9 11v17"/><path d="M40 4v25" /><path d="M40 8h4M40 15h4M40 22h4" stroke-opacity="0.6"/></svg>',
  'rot-izq':
    '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M7 12h34M10 12v16M38 12v16"/><path d="M15 6l-3 8M23 6l-3 8M31 6l-3 8" stroke-opacity="0.85"/></svg>',
  'rot-der':
    '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M7 12h34M10 12v16M38 12v16"/><path d="M13 6l3 8M21 6l3 8M29 6l3 8" stroke-opacity="0.85"/></svg>',
  led: '<svg viewBox="0 0 48 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M8 13h32"/><path d="M16 18l-2 5M24 18v5M32 18l2 5" stroke-opacity="0.85"/></svg>',
};

// ---------- helpers DOM ----------
function el(tag: string, className = ''): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}
function textEl(tag: string, className: string, text: string): HTMLElement {
  const node = el(tag, className);
  node.textContent = text;
  return node;
}
function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = className;
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}
